import { useEffect, useRef, useState } from "react";
import LocalStoragePersistence from "../../../infra/persistence/local-storage";
import JWT from "../../../infra/encoder/jwt";
import AxiosTokenApi, { readAuthToken } from "../../../infra/api/axios-token-api";
import { reportLogoutAndWait } from "../../General/userActivityApi";

const _15_MINUTES = 900_000;
const _5_MINUTES = 300_000;
const _4_HOUR = 14_400_000;

// Renew once the last sixth of the token's life remains. Derived from the token rather than
// fixed, because the server lifetime is configurable per environment (SystemSettings
// SessionTokenLifetimeMinutes) so a short test session would otherwise sit permanently inside
// a fixed 40 minute window and renew on every check and every keypress.
// On the standard 4 hour token this is 40 minutes, exactly the previous behaviour.
const RENEW_AT_FRACTION_OF_LIFETIME = 1 / 6;

// Used when the token carries no usable iat/exp. 40 minutes, the 4 hour default's share.
const DEFAULT_RENEW_THRESHOLD = 2_400_000;

const ACTIVITY_KEY = "activity-timestamp";

/**
 * How much remaining life should trigger a renewal, scaled to the token in hand.
 * Never below the check interval, or a token could expire between two checks.
 * @param {JWT} jwtToken
 * @returns {number} milliseconds
 */
function renewThreshold(jwtToken) {
  const lifetime = jwtToken.getLifetime();

  if (!lifetime) return DEFAULT_RENEW_THRESHOLD;

  return Math.max(lifetime * RENEW_AT_FRACTION_OF_LIFETIME, _5_MINUTES);
}

/**
 * AutoLogoff component
 * @param {Object} props
 * @param {number} props.timeout
 * @returns
 */
export default function AutoLogoff({ timeout = _4_HOUR }) {
  const storage = LocalStoragePersistence.getInstance();
  const [lastActivity, setLastActivity] = useState(Date.now());
  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  function resetActivityTimer() {
    const now = Date.now();
    setLastActivity(now);
    localStorage.setItem(ACTIVITY_KEY, now.toString());
  }

  async function logout() {
    // Record the end of the session before the token is thrown away, otherwise the call arrives
    // without credentials and nothing is recorded. If the token has already expired this simply
    // 401s and the session end goes unrecorded, which is the case the presence samples bound.
    await reportLogoutAndWait(true);

    storage.removeItem("gentell-fastcare-authToken");
    storage.removeItem("gentell-fastcare-authUser");
    window.location.assign("/logout?se=ex");
  }

  async function refresh() {
    const refreshTokenApi = new AxiosTokenApi(storage, new JWT());
    await refreshTokenApi.refreshToken();
  }

  async function validateToken() {
    // Same accessor as the refresh call. jwt-decode happened to survive the unparsed, quoted form
    // (the quotes sit outside the payload segment it decodes), which is why expiry detection kept
    // working while the refresh silently failed. Reading it one way everywhere removes that trap.
    const token = readAuthToken(storage);

    if (token) {
      const jwtToken = new JWT();

      jwtToken.setToken(token);

      if (jwtToken.isExpired()) {
        await logout();
      } else if (jwtToken.itWillExpireSoon(renewThreshold(jwtToken))) {
        await refresh();
      }
    }
  }

  async function resetTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      await validateToken();
    }, 1000);

    await validateToken();
  }

  async function checkInactivity() {
    const hasToken = readAuthToken(storage);

    const now = Date.now();

    const lastActivityTimestamp = parseInt(
      localStorage.getItem(ACTIVITY_KEY),
      10
    );

    await validateToken();

    if (now - lastActivityTimestamp >= timeout && hasToken) {
      await logout();
    }
  }

  useEffect(() => {
    function handleActivity() {
      resetActivityTimer();
      resetTimer();
    }

    function handleStorageEvent(event) {
      if (event.key === ACTIVITY_KEY) {
        setLastActivity(parseInt(event.newValue, 10));
        resetTimer();
      }
    }

    window.addEventListener("mouseover", handleActivity);
    window.addEventListener("keypress", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("storage", handleStorageEvent);

    const intervalToCheckInactivity = setInterval(checkInactivity, 1000);

    intervalRef.current = setInterval(validateToken, _5_MINUTES);

    return () => {
      window.removeEventListener("mouseover", handleActivity);
      window.removeEventListener("keypress", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("storage", handleStorageEvent);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      clearInterval(intervalToCheckInactivity);
    };
  }, [timeout]);

  return null;
}
